import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api, getSessionCookieToken, type OrgNotificationSettings } from '../api/client'
import LoadingOverlay from './LoadingOverlay'

const DEFAULT_SETTINGS: OrgNotificationSettings = {
  slack_connected: false,
  slack_default_channel: '#incidents',
  email_recipients: '',
  notify_patch_pr: true,
  quiet_hours_enabled: false,
  quiet_hours_start: '22:00',
  quiet_hours_end: '08:00',
}

export default function OrgNotificationSettingsPanel() {
  const { getToken } = useAuth()
  const [settings, setSettings] = useState<OrgNotificationSettings>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [slackOAuthConfigured, setSlackOAuthConfigured] = useState<boolean | null>(null)

  async function authToken(): Promise<string> {
    const t = getSessionCookieToken() ?? (await getToken())
    if (!t) throw new Error('Not authenticated')
    return t
  }

  async function handleConnectSlack() {
    setConnecting(true)
    setConnectError(null)
    try {
      const t = await authToken()
      const returnUri = window.location.href
      const { install_url } = await api.getSlackInstallUrl(t, returnUri)
      window.location.href = install_url
    } catch (err) {
      setConnectError((err as Error).message)
      setConnecting(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      const [settingsResult, statusResult] = await Promise.allSettled([
        authToken().then(t => api.getNotificationSettings(t)),
        api.getSlackStatus(),
      ])
      if (settingsResult.status === 'fulfilled') {
        setSettings(settingsResult.value)
      }
      if (statusResult.status === 'fulfilled') {
        setSlackOAuthConfigured(statusResult.value.oauth_configured)
      } else {
        setSlackOAuthConfigured(false)
      }
      setLoading(false)
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function update<K extends keyof OrgNotificationSettings>(key: K, value: OrgNotificationSettings[K]) {
    setSettings(prev => ({ ...prev, [key]: value }))
    setSaveOk(false)
    setSaveError(null)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSaveOk(false)
    setSaveError(null)
    try {
      const t = await authToken()
      const saved = await api.saveNotificationSettings(t, settings)
      setSettings(saved)
      setSaveOk(true)
    } catch (err) {
      setSaveError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <LoadingOverlay minHeight="240px" />
  }

  return (
    <section className="notif-settings-panel">
      <div className="notif-settings-header">
        <h2 className="integration-section-title" style={{ margin: 0 }}>Notification Settings</h2>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
          Control how SelfHeal notifies your team about incidents, fixes, and pull requests.
        </p>
      </div>

      <form onSubmit={handleSave} className="notif-settings-form">

        <div className="notif-settings-grid">

        {/* Slack */}
        <fieldset className="notif-settings-group">
          <legend className="notif-settings-legend">
            <SlackIcon />
            Slack
          </legend>
          <div className="notif-settings-row">
            <span className="notif-settings-label">Workspace connection</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {settings.slack_connected ? (
                <span className="health-badge health-ok">Connected</span>
              ) : (
                <span className="health-badge health-missing">Not connected</span>
              )}
              {slackOAuthConfigured === false ? (
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Slack OAuth is not set up on this server. Ask your admin to set{' '}
                  <code>SLACK_CLIENT_ID</code>, <code>SLACK_CLIENT_SECRET</code>, and{' '}
                  <code>SLACK_REDIRECT_URI</code>.
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="oauth-connect-btn"
                    style={{ padding: '4px 14px', fontSize: '12px' }}
                    onClick={handleConnectSlack}
                    disabled={connecting || slackOAuthConfigured === null}
                  >
                    {connecting ? 'Redirecting…' : settings.slack_connected ? 'Reconnect' : 'Connect Slack'}
                  </button>
                  {connectError && (
                    <span className="cred-error" style={{ fontSize: '11px' }}>{connectError}</span>
                  )}
                </>
              )}
            </span>
          </div>
          <div className="notif-settings-row-input">
            <label className="notif-settings-label-stacked" htmlFor="slack-channel">Default channel</label>
            <input
              id="slack-channel"
              className="cred-input"
              style={{ width: '100%', maxWidth: '320px', marginTop: '4px' }}
              type="text"
              value={settings.slack_default_channel}
              onChange={e => update('slack_default_channel', e.target.value)}
              placeholder="#incidents"
              disabled={!settings.slack_connected}
            />
          </div>
        </fieldset>

        {/* Email */}
        <fieldset className="notif-settings-group">
          <legend className="notif-settings-legend">
            <EmailIcon />
            Email fallback
          </legend>
          <div className="notif-settings-row-input">
            <label className="notif-settings-label-stacked" htmlFor="email-recipients">
              Recipients <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '12px' }}>(comma-separated)</span>
            </label>
            <input
              id="email-recipients"
              className="cred-input"
              style={{ width: '100%', maxWidth: '480px', marginTop: '4px' }}
              type="text"
              value={settings.email_recipients}
              onChange={e => update('email_recipients', e.target.value)}
              placeholder="oncall@example.com, sre-team@example.com"
            />
          </div>
          <p className="notif-settings-hint" style={{ marginTop: '2px' }}>
            Backup when Slack is not connected. You'll also always receive in-app notifications.
          </p>
        </fieldset>

        {/* Events */}
        <fieldset className="notif-settings-group">
          <legend className="notif-settings-legend">
            <EventsIcon />
            Events
          </legend>
          <div className="notif-settings-row">
            <span className="notif-settings-label">RCA ready</span>
            <span className="health-badge health-ok" style={{ fontSize: 11 }}>Always on</span>
          </div>
          <div className="notif-settings-row">
            <label className="notif-settings-label" htmlFor="notify-patch-pr" style={{ cursor: 'pointer' }}>
              Patch & PR notifications
            </label>
            <label className="notif-toggle" aria-label="Enable patch and PR notifications">
              <input
                id="notify-patch-pr"
                type="checkbox"
                checked={settings.notify_patch_pr}
                onChange={e => update('notify_patch_pr', e.target.checked)}
              />
              <span className="notif-toggle-track" />
            </label>
          </div>
        </fieldset>

        {/* Quiet hours */}
        <fieldset className="notif-settings-group">
          <legend className="notif-settings-legend">
            <MoonIcon />
            Quiet hours
          </legend>
          <p className="notif-settings-hint" style={{ marginBottom: 10 }}>
            Non-critical alerts are held during quiet hours. Critical alerts always get through immediately.
          </p>
          <div className="notif-settings-row">
            <label className="notif-settings-label" htmlFor="quiet-hours-toggle" style={{ cursor: 'pointer' }}>
              Enable quiet hours
            </label>
            <label className="notif-toggle" aria-label="Enable quiet hours">
              <input
                id="quiet-hours-toggle"
                type="checkbox"
                checked={settings.quiet_hours_enabled}
                onChange={e => update('quiet_hours_enabled', e.target.checked)}
              />
              <span className="notif-toggle-track" />
            </label>
          </div>
          {settings.quiet_hours_enabled && (
            <div className="notif-settings-row-input" style={{ marginTop: '4px' }}>
              <span className="notif-settings-label-stacked">Window</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '4px' }}>
                <input
                  className="cred-input"
                  style={{ width: 110 }}
                  type="time"
                  value={settings.quiet_hours_start}
                  onChange={e => update('quiet_hours_start', e.target.value)}
                  aria-label="Quiet hours start"
                />
                <span style={{ color: 'var(--text-muted)' }}>to</span>
                <input
                  className="cred-input"
                  style={{ width: 110 }}
                  type="time"
                  value={settings.quiet_hours_end}
                  onChange={e => update('quiet_hours_end', e.target.value)}
                  aria-label="Quiet hours end"
                />
              </div>
            </div>
          )}
        </fieldset>

        </div>

        {saveError && <p className="cred-error" style={{ margin: 0 }}>{saveError}</p>}
        {saveOk && <p className="cred-success" style={{ margin: 0 }}>Settings saved.</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="cred-save-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </form>
    </section>
  )
}

function SlackIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="1" y="5" width="3" height="4" rx="1.5" fill="currentColor" />
      <rect x="5" y="1" width="4" height="3" rx="1.5" fill="currentColor" />
      <rect x="10" y="5" width="3" height="4" rx="1.5" fill="currentColor" />
      <rect x="5" y="10" width="4" height="3" rx="1.5" fill="currentColor" />
    </svg>
  )
}

function EmailIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1.5 3.5 7 7.5l5.5-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function EventsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <path d="M7 1v3M7 10v3M1 7h3M10 7h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <path d="M11 8.5A5 5 0 0 1 5.5 3a5 5 0 1 0 5.5 5.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  )
}
